import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatError } from "../../lib/api";
import { toast } from "sonner";
import {
  Phone, PhoneOff, PhoneCall, Mic, MicOff, Volume2, Loader2, Sparkles,
  Calendar, Clock, Timer, Target, Bot, CheckCircle2, ArrowLeft, Pencil,
} from "lucide-react";

const maskPhone = (p) => {
  if (!p) return "+•• •••••• ••••";
  const digits = p.replace(/\D/g, "");
  const last4 = digits.slice(-4);
  return `+${digits.slice(0, 2)} •••••• ${last4}`;
};

const CALL_STATE_LABEL = {
  calling: "Calling…",
  connecting: "Connecting…",
  connected: "Connected",
  speaking: "AI Speaking",
  listening: "Listening to you…",
  processing: "Processing response…",
};

export default function VoiceInterview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [iv, setIv] = useState(null);
  const [view, setView] = useState("prep"); // prep | call
  const [callState, setCallState] = useState("calling");
  const [qIdx, setQIdx] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const [seconds, setSeconds] = useState(0);
  const [mute, setMute] = useState(false);
  const [speaker, setSpeaker] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [phone, setPhone] = useState("");
  const [editPhone, setEditPhone] = useState(false);
  const scrollRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get(`/interviews/${id}`).then((r) => {
      if (r.data.status === "completed") { navigate(`/candidate/result/${id}`); return; }
      if (r.data.mode !== "voice") { navigate(`/candidate/interview/${id}`, { replace: true }); return; }
      setIv(r.data);
      setPhone(r.data.phone || "");
    }).catch(() => { toast.error("Interview not found"); navigate("/candidate"); });
  }, [id, navigate]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [transcript]);

  useEffect(() => {
    if (view === "call") {
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [view]);

  if (!iv) return <div className="min-h-screen grid place-items-center bg-slate-950"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const questions = iv.questions || [];
  const total = questions.length;
  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const say = (who, text, phase) => setTranscript((t) => [...t, { who, text, phase }]);

  const runCall = async () => {
    setView("call");
    setCallState("calling");
    await wait(2000);
    setCallState("connecting");
    await wait(1200);
    setCallState("connected");
    say("interviewer", `Hello ${iv.candidate_name?.split(" ")[0] || "there"}. Welcome to your Mock2Crack AI interview. Are you ready to begin?`, "Introduction");
    await wait(1800);
    for (let i = 0; i < total; i++) {
      setQIdx(i);
      setCallState("speaking");
      say("interviewer", questions[i].question, questions[i].phase);
      await wait(2400);
      setCallState("listening");
      say("candidate", `(You respond to: ${questions[i].phase})`);
      await wait(1900);
      setCallState("processing");
      await wait(1000);
    }
    setCallState("connected");
    say("interviewer", "Thank you. That concludes your interview. Your scorecard will be ready shortly.", "Closing");
    await wait(1600);
    endCall();
  };

  const endCall = async () => {
    clearInterval(timerRef.current);
    setAnalyzing(true);
    try {
      await wait(2200);
      await api.post(`/interviews/${id}/complete`, { coding_language: "python" });
      navigate(`/candidate/result/${id}`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      setAnalyzing(false);
    }
  };

  const savePhone = async () => {
    try {
      await api.put("/candidate/profile", { phone });
      setEditPhone(false);
      toast.success("Phone number updated");
    } catch { toast.error("Could not update number"); }
  };

  if (analyzing) {
    return (
      <div className="fixed inset-0 bg-slate-950 text-white grid place-items-center z-50">
        <div className="text-center">
          <div className="relative h-20 w-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-white/10" />
            <div className="absolute inset-0 rounded-full border-4 border-brand border-t-transparent animate-spin" />
            <Sparkles className="absolute inset-0 m-auto h-7 w-7 text-brand" />
          </div>
          <div className="font-heading text-2xl font-bold">Analyzing Your Voice Interview…</div>
          <p className="text-slate-400 text-sm mt-2">Scoring communication, clarity and role fit.</p>
        </div>
      </div>
    );
  }

  // ---------- PREP VIEW ----------
  if (view === "prep") {
    return (
      <div className="min-h-screen bg-slate-950 text-white px-5 py-10">
        <div className="max-w-lg mx-auto fade-up">
          <button onClick={() => navigate("/candidate")} className="inline-flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-white mb-5"><ArrowLeft className="h-4 w-4" /> Back to Dashboard</button>
          <div className="bg-slate-900 rounded-3xl border border-white/10 p-8">
            <div className="h-14 w-14 rounded-2xl bg-emerald-500/20 grid place-items-center mb-5 ring-2 ring-emerald-500/30"><Phone className="h-7 w-7 text-emerald-400" /></div>
            <h1 className="font-heading text-2xl font-bold">Your AI Voice Interview</h1>
            <p className="text-slate-400 text-sm mt-1">Your Mock2Crack AI interviewer will call you at your scheduled time.</p>

            <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300" data-testid="voice-status-badge">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Scheduled
            </div>

            <div className="mt-5 rounded-2xl border border-white/10 divide-y divide-white/5">
              {[
                ["Job Role", iv.role_name, Target],
                ["Interview", "📞 AI Voice Interview", Phone],
                ["Date", iv.date, Calendar],
                ["Time", iv.time, Clock],
                ["Duration", `${iv.duration} minutes`, Timer],
                ["Panel", iv.panel_name, Bot],
              ].map(([k, v, Icon]) => (
                <div key={k} className="flex items-center justify-between px-4 py-3 text-sm">
                  <span className="inline-flex items-center gap-2 text-slate-400"><Icon className="h-4 w-4" /> {k}</span>
                  <span className="font-semibold">{v}</span>
                </div>
              ))}
              {/* Phone row */}
              <div className="px-4 py-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="inline-flex items-center gap-2 text-slate-400"><PhoneCall className="h-4 w-4" /> Phone</span>
                  {!editPhone ? (
                    <span className="font-semibold num flex items-center gap-2" data-testid="voice-phone-masked">{maskPhone(phone)}
                      <button onClick={() => setEditPhone(true)} data-testid="change-number" className="text-brand hover:text-brand-hover"><Pencil className="h-3.5 w-3.5" /></button>
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <input data-testid="voice-phone-edit" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-40 rounded-lg bg-slate-800 border border-white/10 px-2.5 py-1.5 text-xs num text-white focus:outline-none focus:border-brand" />
                      <button onClick={savePhone} data-testid="save-number" className="text-xs font-bold text-emerald-400">Save</button>
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-xl bg-amber-500/10 border border-amber-500/20 px-4 py-3 text-xs text-amber-200">
              Demo mode — the phone call is simulated in your browser. No real call is placed.
            </div>

            <button data-testid="start-demo-voice" onClick={runCall}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3.5 rounded-full transition-colors">
              <PhoneCall className="h-4 w-4" /> Start Demo Voice Interview
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- CALL VIEW ----------
  const connecting = callState === "calling" || callState === "connecting";
  return (
    <div className="fixed inset-0 bg-gradient-to-b from-slate-950 to-slate-900 text-white flex flex-col">
      {/* status bar */}
      <div className="px-5 pt-10 pb-6 text-center shrink-0">
        <div className="relative mx-auto h-28 w-28 mb-4">
          <div className={`absolute inset-0 rounded-full bg-brand/20 ${callState === "speaking" ? "animate-ping" : ""}`} />
          <div className="absolute inset-0 rounded-full bg-slate-800 grid place-items-center ring-4 ring-brand/30">
            <Bot className="h-12 w-12 text-brand" />
          </div>
        </div>
        <div className="font-heading text-xl font-bold">Mock2Crack AI Interviewer</div>
        <div className="text-sm text-brand font-semibold mt-1 flex items-center justify-center gap-2" data-testid="call-state">
          {connecting && <Loader2 className="h-4 w-4 animate-spin" />}
          {callState === "listening" && <Mic className="h-4 w-4 animate-pulse" />}
          {CALL_STATE_LABEL[callState]}
        </div>
        <div className="text-xs text-slate-400 num mt-1">{fmt(seconds)}</div>
        {!connecting && <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mt-2 num">Question {Math.min(qIdx + 1, total)} of {total}</div>}
      </div>

      {/* transcript */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-5 space-y-3 max-w-lg mx-auto w-full">
        {transcript.map((m, i) => (
          <div key={i} className={`flex ${m.who === "candidate" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.who === "candidate" ? "bg-brand text-white" : "bg-white/10 text-slate-100"}`}>
              {m.phase && <div className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-0.5">{m.phase}</div>}
              {m.text}
            </div>
          </div>
        ))}
      </div>

      {/* controls */}
      <div className="shrink-0 px-5 py-8">
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => setMute(!mute)} data-testid="voice-mute" className={`h-14 w-14 rounded-full grid place-items-center transition-colors ${mute ? "bg-red-500 text-white" : "bg-white/10 text-white hover:bg-white/20"}`}>
            {mute ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
          </button>
          <button onClick={endCall} data-testid="voice-end-call" className="h-16 w-16 rounded-full bg-red-500 hover:bg-red-600 grid place-items-center transition-colors">
            <PhoneOff className="h-7 w-7 text-white" />
          </button>
          <button onClick={() => setSpeaker(!speaker)} data-testid="voice-speaker" className={`h-14 w-14 rounded-full grid place-items-center transition-colors ${speaker ? "bg-white/10 text-white hover:bg-white/20" : "bg-white/5 text-slate-500"}`}>
            <Volume2 className="h-6 w-6" />
          </button>
        </div>
        <div className="text-center text-[11px] text-slate-500 mt-4 flex items-center justify-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5" /> Voice-only interview — no video required
        </div>
      </div>
    </div>
  );
}

function wait(ms) { return new Promise((r) => setTimeout(r, ms)); }
